import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { EOS_TODO_PRIORITIES, EOS_TODO_PRIORITY_LABELS, type EosTodoFilters, type EosTodoPriority } from '../types';

interface Props {
  filters: EosTodoFilters;
  onChange: (next: EosTodoFilters) => void;
  ownerOptions?: { id: string; label: string }[];
}

export function TodoFilters({ filters, onChange, ownerOptions = [] }: Props) {
  const selectedOwnerIds = filters.owner_ids ?? [];

  const toggleOwner = (id: string) => {
    const next = selectedOwnerIds.includes(id)
      ? selectedOwnerIds.filter((o) => o !== id)
      : [...selectedOwnerIds, id];
    onChange({ ...filters, owner_ids: next.length > 0 ? next : undefined });
  };

  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="flex flex-col gap-1">
        <Label htmlFor="todo-search" className="text-xs text-muted-foreground">Search</Label>
        <Input
          id="todo-search"
          value={filters.search ?? ''}
          placeholder="Title contains…"
          className="w-56"
          onChange={(e) => onChange({ ...filters, search: e.target.value || undefined })}
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">Priority</Label>
        <Select
          value={filters.priority ?? 'any'}
          onValueChange={(v) => onChange({ ...filters, priority: v === 'any' ? undefined : (v as EosTodoPriority) })}
        >
          <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any</SelectItem>
            {EOS_TODO_PRIORITIES.map(p => (
              <SelectItem key={p} value={p}>{EOS_TODO_PRIORITY_LABELS[p]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {ownerOptions.length > 0 && (
        <div className="flex flex-col gap-1">
          <Label className="text-xs text-muted-foreground">Owner</Label>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="w-40 justify-start text-xs font-normal">
                {selectedOwnerIds.length === 0
                  ? 'All Owners'
                  : selectedOwnerIds.length === 1
                    ? (ownerOptions.find((o) => o.id === selectedOwnerIds[0])?.label ?? '1 owner')
                    : `${selectedOwnerIds.length} owners`}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48 max-h-64 overflow-y-auto">
              {ownerOptions.map((o) => (
                <DropdownMenuCheckboxItem
                  key={o.id}
                  checked={selectedOwnerIds.includes(o.id)}
                  onCheckedChange={() => toggleOwner(o.id)}
                >
                  {o.label}
                </DropdownMenuCheckboxItem>
              ))}
              {selectedOwnerIds.length > 0 && (
                <DropdownMenuItem
                  className="text-xs text-muted-foreground mt-1 border-t"
                  onClick={() => onChange({ ...filters, owner_ids: undefined })}
                >
                  Clear
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      <div className="flex items-center gap-2">
        <Switch
          id="include-completed"
          checked={!!filters.include_completed}
          onCheckedChange={(v) => onChange({ ...filters, include_completed: v })}
        />
        <Label htmlFor="include-completed" className="text-xs">Show completed</Label>
      </div>

      <div className="flex items-center gap-2">
        <Switch
          id="include-cancelled"
          checked={!!filters.include_cancelled}
          onCheckedChange={(v) => onChange({ ...filters, include_cancelled: v })}
        />
        <Label htmlFor="include-cancelled" className="text-xs">Show cancelled</Label>
      </div>
    </div>
  );
}
